import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

const STORAGE_KEY = 'matchmaker:last-state:v1';

function defaultSetup() {
  return {
    competitionType: 'knockout',
    playerListText: '',
    groupCount: 4,
    swissRounds: 3,
    shuffle: true,
    autoBalanceGroups: true,
  };
}

function loadSetup() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSetup();
    const parsed = JSON.parse(raw);
    return {
      ...defaultSetup(),
      ...parsed,
    };
  } catch (error) {
    return defaultSetup();
  }
}

function saveSetup(setup) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(setup));
  } catch (error) {
    // ignore storage failures
  }
}

function getPrintWindowContent(element) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>MatchMaker Print View</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 20px; color: #111827; }
      h1, h2, h3, h4 { margin: 0 0 12px; }
      * { box-sizing: border-box; }
      table { border-collapse: collapse; width: 100%; margin-bottom: 14px; }
      th, td { border: 1px solid #cbd5e1; padding: 6px 8px; text-align: left; font-size: 13px; }
      .round { margin-bottom: 16px; }
      .pair { padding: 6px 8px; border: 1px solid #e2e8f0; margin-bottom: 6px; }
      .groups { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; }
      .group { border: 1px solid #cbd5e1; padding: 10px; }
      .mm-bracket { display: flex; gap: 14px; align-items: flex-start; min-width: 760px; }
      .mm-round { min-width: 220px; flex: 1; }
      .mm-round-title { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 8px; color: #475569; }
      .mm-round-list { position: relative; }
      .mm-match { border: 1px solid #cbd5e1; border-radius: 10px; padding: 8px; margin-bottom: 10px; background: #fff; break-inside: avoid; }
      .mm-match-label { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: #64748b; margin-bottom: 6px; }
      .mm-team-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
      .mm-team-name { flex: 1; min-width: 0; }
      .mm-vs { font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: #94a3b8; margin: 4px 0; }
      .mm-score-box { width: 34px; height: 20px; border: 1px solid #94a3b8; border-radius: 4px; display: inline-block; }
      .mm-score-dual { display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
      .mm-score-sep { color: #64748b; font-size: 12px; }
      .mm-table { width: 100%; border-collapse: collapse; }
      .mm-table th, .mm-table td { border: 1px solid #cbd5e1; padding: 6px 8px; font-size: 13px; }
      .mm-print-note { margin-top: 8px; margin-bottom: 10px; font-size: 11px; color: #64748b; }
    </style>
  </head>
  <body>${element.innerHTML}</body>
</html>`;
}

function scoreTemplate() {
  return (
    <span className="mm-score-dual" aria-label="score entry">
      <span className="mm-score-box" />
      <span className="mm-score-sep">-</span>
      <span className="mm-score-box" />
    </span>
  );
}

function bracketColumns(rounds = []) {
  return (
    <div className="overflow-x-auto">
      <div className="mm-bracket flex min-w-[760px] gap-4 pb-2">
        {rounds.map((round, roundIndex) => {
          const roundGap = 10 + (roundIndex * 22);
          const roundTopPadding = roundIndex > 0 ? Math.floor(roundGap / 2) : 0;

          return (
          <div key={round.name} className="mm-round min-w-[220px] flex-1">
            <h4 className="mm-round-title mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">{round.name}</h4>
            <div className="mm-round-list" style={{ paddingTop: `${roundTopPadding}px` }}>
              {(round.matches || []).map((match, index) => (
                <div
                  key={match.id}
                  className="mm-match rounded-xl border border-slate-200 bg-white p-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                  style={{ marginBottom: `${roundGap}px` }}
                >
                  <div className="mm-match-label mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">M{index + 1}</div>
                  <div className="mm-team-row">
                    <div className="mm-team-name">{match.home.name}</div>
                    {scoreTemplate()}
                  </div>
                  <div className="mm-vs my-1 text-[11px] uppercase text-slate-400">vs</div>
                  <div className="mm-team-row">
                    <div className="mm-team-name">{match.away.name}</div>
                    {scoreTemplate()}
                  </div>
                  {match.bye ? <div className="mt-1 text-[11px] text-amber-600 dark:text-amber-300">Bye match</div> : null}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

function roundRobinTable(draw) {
  return (
    <div className="space-y-4">
      {(draw.rounds || []).map((round) => (
        <div key={round.name} className="rounded-2xl border border-slate-200 bg-white/80 p-3 dark:border-slate-700 dark:bg-slate-900/60">
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">{round.name}</h4>
          <div className="overflow-x-auto">
            <table className="mm-table min-w-[420px] w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-300">
                  <th className="py-1 pr-3">Home</th>
                  <th className="py-1 pr-3">Away</th>
                  <th className="py-1 pr-3">Score</th>
                </tr>
              </thead>
              <tbody>
                {(round.fixtures || []).map((fixture) => (
                  <tr key={fixture.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1 pr-3">{fixture.home.name}</td>
                    <td className="py-1 pr-3">{fixture.away.name}</td>
                    <td className="py-1 pr-3">{scoreTemplate()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 dark:border-slate-700 dark:bg-slate-900/60">
        <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Standings Template</h4>
        <div className="overflow-x-auto">
          <table className="min-w-[520px] w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 dark:text-slate-300">
                <th className="py-1 pr-3">Team</th>
                <th className="py-1 pr-3">P</th>
                <th className="py-1 pr-3">W</th>
                <th className="py-1 pr-3">D</th>
                <th className="py-1 pr-3">L</th>
                <th className="py-1 pr-3">Pts</th>
              </tr>
            </thead>
            <tbody>
              {(draw.standingsTemplate || []).map((row) => (
                <tr key={row.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="py-1 pr-3">{row.name}</td>
                  <td className="py-1 pr-3">{row.played}</td>
                  <td className="py-1 pr-3">{row.won}</td>
                  <td className="py-1 pr-3">{row.draw}</td>
                  <td className="py-1 pr-3">{row.lost}</td>
                  <td className="py-1 pr-3">{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function groupsView(draw) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {(draw.groups || []).map((group) => (
          <div key={group.id} className="rounded-2xl border border-slate-200 bg-white/80 p-3 dark:border-slate-700 dark:bg-slate-900/60">
            <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Group {group.label}</h4>
            <ol className="space-y-1 text-sm">
              {(group.entrants || []).map((entrant) => (
                <li key={entrant.id}>{entrant.name}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>
      <div>
        <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Knockout Seeding</h4>
        {bracketColumns(draw.knockout?.rounds || [])}
      </div>
    </div>
  );
}

function swissView(draw) {
  return (
    <div className="space-y-4">
      {(draw.rounds || []).map((round) => (
        <div key={round.name} className="rounded-2xl border border-slate-200 bg-white/80 p-3 dark:border-slate-700 dark:bg-slate-900/60">
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">{round.name}</h4>
          <div className="overflow-x-auto">
            <table className="mm-table min-w-[520px] w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-300">
                  <th className="py-1 pr-3">Board</th>
                  <th className="py-1 pr-3">Home</th>
                  <th className="py-1 pr-3">Away</th>
                  <th className="py-1 pr-3">Score</th>
                </tr>
              </thead>
              <tbody>
                {(round.pairings || []).map((pairing, index) => (
                  <tr key={pairing.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1 pr-3">{index + 1}</td>
                    <td className="py-1 pr-3">{pairing.home.name}</td>
                    <td className="py-1 pr-3">{pairing.away.name}</td>
                    <td className="py-1 pr-3">{scoreTemplate()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 dark:border-slate-700 dark:bg-slate-900/60">
        <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">Standings Template</h4>
        <div className="overflow-x-auto">
          <table className="min-w-[420px] w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 dark:text-slate-300">
                <th className="py-1 pr-3">Team</th>
                <th className="py-1 pr-3">W</th>
                <th className="py-1 pr-3">D</th>
                <th className="py-1 pr-3">L</th>
                <th className="py-1 pr-3">Pts</th>
              </tr>
            </thead>
            <tbody>
              {(draw.standingsTemplate || []).map((row) => (
                <tr key={row.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="py-1 pr-3">{row.name}</td>
                  <td className="py-1 pr-3">{row.wins}</td>
                  <td className="py-1 pr-3">{row.draws}</td>
                  <td className="py-1 pr-3">{row.losses}</td>
                  <td className="py-1 pr-3">{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function randomPairingsView(draw) {
  return (
    <div className="space-y-4">
      {(draw.rounds || []).map((round, roundIndex) => (
        <div key={round.name || `round-${roundIndex}`} className="rounded-2xl border border-slate-200 bg-white/80 p-3 dark:border-slate-700 dark:bg-slate-900/60">
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-300">{round.name || `Round ${roundIndex + 1}`}</h4>
          <div className="overflow-x-auto">
            <table className="mm-table min-w-[520px] w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-300">
                  <th className="py-1 pr-3">Board</th>
                  <th className="py-1 pr-3">Home</th>
                  <th className="py-1 pr-3">Away</th>
                  <th className="py-1 pr-3">Score</th>
                </tr>
              </thead>
              <tbody>
                {(round.pairings || []).map((pairing, index) => (
                  <tr key={pairing.id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1 pr-3">{index + 1}</td>
                    <td className="py-1 pr-3">{pairing.home.name}</td>
                    <td className="py-1 pr-3">{pairing.away.name}</td>
                    <td className="py-1 pr-3">{scoreTemplate()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="mm-print-note rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300">
        Random pairings formatted like Swiss sheets for easy score entry.
      </div>
    </div>
  );
}

function renderOutput(draw) {
  if (!draw) return null;

  if (draw.type === 'knockout') return bracketColumns(draw.rounds || []);
  if (draw.type === 'roundRobin') return roundRobinTable(draw);
  if (draw.type === 'groupsKnockout') return groupsView(draw);
  if (draw.type === 'swiss') return swissView(draw);
  if (draw.type === 'randomPairings') return randomPairingsView(draw);

  return null;
}

function MatchMakerGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const [setup, setSetup] = useState(() => loadSetup());
  const [localSavedDraw, setLocalSavedDraw] = useState(null);
  const [copied, setCopied] = useState('');
  const printRef = React.useRef(null);

  const myId = user?.socketId || user?.userId || null;
  const hostSocketId = gameState?.hostSocketId || null;
  const isHost = !!myId && !!hostSocketId && myId === hostSocketId;

  const drawOutput = gameState?.drawOutput;
  const warnings = Array.isArray(gameState?.warnings) ? gameState.warnings : [];
  const errorMessage = gameState?.errorMessage || '';
  const hostSetup = useMemo(() => {
    const settings = gameState?.settings || {};
    return {
      competitionType: settings.competitionType || setup.competitionType,
      playerListText: typeof gameState?.playerListText === 'string' ? gameState.playerListText : setup.playerListText,
      groupCount: Number(settings.groupCount || setup.groupCount || 4),
      swissRounds: Number(settings.swissRounds || setup.swissRounds || 3),
      shuffle: typeof settings.shuffle === 'boolean' ? settings.shuffle : !!setup.shuffle,
      autoBalanceGroups: typeof settings.autoBalanceGroups === 'boolean'
        ? settings.autoBalanceGroups
        : !!setup.autoBalanceGroups,
    };
  }, [gameState?.settings, gameState?.playerListText, setup]);

  const dispatchAction = useCallback(async (action) => {
    if (typeof onGameAction === 'function') return onGameAction(action);
    return submitAction(action);
  }, [onGameAction, submitAction]);

  useEffect(() => {
    saveSetup(setup);
  }, [setup]);

  useEffect(() => {
    if (!isHost) return undefined;
    const timer = setTimeout(() => {
      dispatchAction({
        type: 'updateSetup',
        settings: {
          competitionType: setup.competitionType,
          groupCount: Number(setup.groupCount) || 4,
          swissRounds: Number(setup.swissRounds) || 3,
          shuffle: !!setup.shuffle,
          autoBalanceGroups: !!setup.autoBalanceGroups,
        },
        playerListText: setup.playerListText,
      });
    }, 200);

    return () => clearTimeout(timer);
  }, [
    dispatchAction,
    isHost,
    setup.competitionType,
    setup.groupCount,
    setup.swissRounds,
    setup.shuffle,
    setup.autoBalanceGroups,
    setup.playerListText,
  ]);

  useEffect(() => {
    if (!gameState?.drawOutput) return;
    setLocalSavedDraw(gameState.drawOutput);
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const existing = raw ? JSON.parse(raw) : {};
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...existing,
        lastGeneratedDraw: gameState.drawOutput,
      }));
    } catch (error) {
      // ignore
    }
  }, [gameState?.drawOutput]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const existing = raw ? JSON.parse(raw) : {};
      if (existing.lastGeneratedDraw) setLocalSavedDraw(existing.lastGeneratedDraw);
    } catch (error) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(''), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const parsedCount = useMemo(
    () => String(setup.playerListText || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean).length,
    [setup.playerListText]
  );

  const generatePayload = useMemo(() => ({
    competitionType: setup.competitionType,
    groupCount: Number(setup.groupCount) || 4,
    swissRounds: Number(setup.swissRounds) || 3,
    shuffle: !!setup.shuffle,
    autoBalanceGroups: !!setup.autoBalanceGroups,
  }), [setup]);

  const onGenerate = async () => {
    if (!isHost) return;
    await dispatchAction({
      type: 'generateDraw',
      settings: generatePayload,
      playerListText: setup.playerListText,
    });
  };

  const onRegenerate = async () => {
    if (!isHost) return;
    await dispatchAction({
      type: 'regenerateDraw',
      settings: generatePayload,
      playerListText: setup.playerListText,
    });
  };

  const copyText = async () => {
    const text = gameState?.drawText || JSON.stringify(drawOutput || {}, null, 2);
    await navigator.clipboard.writeText(text);
    setCopied('Text copied');
  };

  const copyJson = async () => {
    await navigator.clipboard.writeText(JSON.stringify(drawOutput || {}, null, 2));
    setCopied('JSON copied');
  };

  const printView = () => {
    if (!printRef.current) return;
    const popup = window.open('', '_blank', 'width=1100,height=800');
    if (!popup) return;
    popup.document.open();
    popup.document.write(getPrintWindowContent(printRef.current));
    popup.document.close();
    popup.focus();
    popup.print();
  };

  return (
    <div className="mx-auto w-full max-w-7xl p-2 text-slate-900 dark:text-slate-100 sm:p-3">
      <div className="rounded-[28px] border border-sky-200/70 bg-gradient-to-br from-sky-50 via-white to-lime-50 p-3 shadow-[0_24px_70px_rgba(15,23,42,0.10)] dark:border-sky-900/60 dark:from-slate-950 dark:via-slate-900 dark:to-sky-950/80 sm:p-4">
        <div className="grid grid-cols-1 gap-4">
          <div className="rounded-3xl border border-slate-200 bg-white/85 p-4 dark:border-slate-800 dark:bg-slate-900/75">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300">Setup</h3>

            {!isHost ? (
              <div className="mt-3 space-y-3">
                <div className="rounded-xl border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                  Host controls setup. This is a live read-only view of host settings.
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800/70">
                  <div><span className="font-semibold">Competition:</span> {hostSetup.competitionType}</div>
                  {hostSetup.competitionType === 'groupsKnockout' && (
                    <div><span className="font-semibold">Groups:</span> {hostSetup.groupCount}</div>
                  )}
                  {hostSetup.competitionType === 'swiss' && (
                    <div><span className="font-semibold">Swiss rounds:</span> {hostSetup.swissRounds}</div>
                  )}
                  <div><span className="font-semibold">Shuffle:</span> {hostSetup.shuffle ? 'Yes' : 'No'}</div>
                  {hostSetup.competitionType === 'groupsKnockout' && (
                    <div><span className="font-semibold">Auto-balance:</span> {hostSetup.autoBalanceGroups ? 'Yes' : 'No'}</div>
                  )}
                </div>

                <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-300">
                  Players / Teams
                  <textarea
                    rows={9}
                    value={hostSetup.playerListText}
                    readOnly
                    className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                  />
                </label>

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300">
                  Parsed lines: {String(hostSetup.playerListText || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean).length}
                </div>
              </div>
            ) : (
            <div className="mt-3 space-y-3">
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-300">
                Competition Type
                <select
                  value={setup.competitionType}
                  onChange={(event) => setSetup((current) => ({ ...current, competitionType: event.target.value }))}
                  disabled={!isHost}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                >
                  <option value="knockout">Knockout</option>
                  <option value="roundRobin">Round Robin</option>
                  <option value="groupsKnockout">Groups {'->'} Knockout</option>
                  <option value="swiss">Swiss</option>
                  <option value="randomPairings">Random Pairings</option>
                </select>
              </label>

              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-300">
                Players / Teams (one per line)
                <textarea
                  rows={9}
                  value={setup.playerListText}
                  onChange={(event) => setSetup((current) => ({ ...current, playerListText: event.target.value }))}
                  disabled={!isHost}
                  placeholder="#1 Team Alpha
#2 Team Bravo
#3 Team Charlie
#4 Team Delta"
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </label>

              <div className="grid grid-cols-2 gap-2">
                {setup.competitionType === 'groupsKnockout' && (
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-300">
                    Groups
                    <input
                      type="number"
                      min={2}
                      max={16}
                      value={setup.groupCount}
                      onChange={(event) => setSetup((current) => ({ ...current, groupCount: event.target.value }))}
                      disabled={!isHost}
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                  </label>
                )}

                {setup.competitionType === 'swiss' && (
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-300">
                    Swiss Rounds
                    <input
                      type="number"
                      min={1}
                      max={30}
                      value={setup.swissRounds}
                      onChange={(event) => setSetup((current) => ({ ...current, swissRounds: event.target.value }))}
                      disabled={!isHost}
                      className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                  </label>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={setup.shuffle}
                    onChange={(event) => setSetup((current) => ({ ...current, shuffle: event.target.checked }))}
                    disabled={!isHost}
                  />
                  <span>Shuffle entrants</span>
                </label>

                {setup.competitionType === 'groupsKnockout' && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={setup.autoBalanceGroups}
                      onChange={(event) => setSetup((current) => ({ ...current, autoBalanceGroups: event.target.checked }))}
                      disabled={!isHost}
                    />
                    <span>Auto-balance groups</span>
                  </label>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300">
                Parsed lines: {parsedCount}
              </div>

              <button
                type="button"
                disabled={!isHost}
                onClick={onGenerate}
                className="w-full rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Generate Draw
              </button>
            </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white/85 p-4 dark:border-slate-800 dark:bg-slate-900/75">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300">Output</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onRegenerate}
                  disabled={!isHost}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  Regenerate
                </button>
                <button
                  type="button"
                  onClick={copyText}
                  disabled={!drawOutput}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  Copy as text
                </button>
                <button
                  type="button"
                  onClick={copyJson}
                  disabled={!drawOutput}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  Copy as JSON
                </button>
                <button
                  type="button"
                  onClick={printView}
                  disabled={!drawOutput}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  Print view
                </button>
              </div>
            </div>

            {copied && (
              <div className="mt-2 rounded-lg bg-emerald-100 px-3 py-1.5 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-100">
                {copied}
              </div>
            )}

            {errorMessage && (
              <div className="mt-3 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
                {errorMessage}
              </div>
            )}

            {warnings.length > 0 && (
              <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                {warnings.map((warning) => (
                  <div key={warning}>- {warning}</div>
                ))}
              </div>
            )}

            <div ref={printRef} className="mt-4">
              {!drawOutput ? (
                <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-300">
                  Generate a draw to see brackets, tables, and pairing output.
                </div>
              ) : (
                <>
                  <h2 className="mb-3 text-xl font-bold text-slate-900 dark:text-slate-100">{drawOutput.title}</h2>
                  {renderOutput(drawOutput)}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

MatchMakerGame.Guide = function MatchMakerGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Match Maker</h3>
      <ul className="list-disc ml-6">
        <li>Enter a list of players or teams, one per line.</li>
        <li>Choose a competition type: Knockout, Round Robin, Groups to Knockout, Swiss, or Random Pairings.</li>
        <li>Click "Generate Draw" to create the matchups.</li>
        <li>View the generated brackets, tables, or pairings in the output section.</li>
        <li>Copy the output as text or JSON, or print it for offline use.</li>
     
      </ul>
    </div>
  );
};

export default MatchMakerGame;

